import { ShipperPaymentTerms } from '../payments/ShipperPaymentTerms';
import { codeLabel } from '../../utils/codeLabel';
import { ShipperBillingConditions } from './ShipperBillingConditions';
import { ShipperRateCard } from './ShipperRateCard';
import { isDemo } from '../../demo/flag';
import { ProfileMediaPreview } from '../../components/domain/ProfileMediaPreview';
import { KycReviewContent, type KycChecksProp } from '../../components/domain/KycReviewContent';
import { kycCheckDisplay } from '../../components/domain/kycCheckDisplay';
import { TdsDeclarationPill } from '../../components/domain/TdsDeclarationPill';
import { DnmRequestFailure } from '../../api/dnmFailure';
import { ErrorState, LoadingState, StaleBanner } from '../../components/ui/AsyncState';
import { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useRecoveryQuery as useQuery } from '../../components/ui/useRecoveryQuery';
import {
  getUserChangeRequests,
  getUserOverview,
  getUserOverviewShipments,
  getUserOverviewBids,
  getUserOverviewVehicles,
  getUserOverviewRatings,
  getUserOverviewDocuments,
  updateUserKyc,
  getVerificationChecks,
  overrideVerificationCheck,
  searchServiceLanes,
  adminDeleteServiceLane,
  getAdminInterests,
} from '../../api/endpoints/admin';
import type {
  AdminKycCheck,
  KycCheckType,
  KycStatus,
  UserRole,
  DriverProfileResponse,
  ShipperProfileResponse,
  FleetManagerProfileResponse,
} from '../../api/types';
import { KpiCard } from '../../components/ui/KpiCard';
import { SectionCard } from '../../components/ui/SectionCard';
import { DataTable } from '../../components/ui/DataTable';
import { Pagination } from '../../components/ui/Pagination';
import { StatusPill } from '../../components/ui/StatusPill';
import { StatusIcon } from '../../components/ui/StatusIcon';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { RefreshButton } from '../../components/ui/RefreshButton';
import { useMutationHook } from '../../hooks/useMutationHook';
import { actionFailureMessage } from '../../api/actionFailure';
import {
  bidStatusDisplay,
  kycStatusColor,
  shipmentStatusColor,
  documentStatusColor,
  interestStatusColor,
} from '../../components/domain/statusColor';
import { UserFormModal } from '../../components/domain/UserFormModal';
import { AddServiceLaneModal } from '../../components/domain/AddServiceLaneModal';
import { InterestDetailModal } from '../interests/InterestDetailModal';
import { curr, dualPrice, partnerPriceLabel } from '../../utils/price';
import { tons } from '../../utils/weight';
import { formatStateWithCapital } from '../../config/indianStates';
import { AccountManagerSection } from '../ownership/AccountManagerSection';
import { shipmentStatusLabel } from '../shipments/shipmentStatusLabel';
import { userDisplayName } from '../../components/domain/partyLabel';
import styles from './UserProfileView.module.css';
import { routeEnd } from '../../utils/routeLabel';
import { RouteText } from '../../components/domain/RouteText';
import { documentSubject } from '../documents/documentSubject';
import {
  historyQueryPolicy,
  referenceQueryPolicy,
  safePagePlaceholder,
  staticQueryPolicy,
} from '../../api/queryPolicies';

type ProfileTab =
  | 'kyc'
  | 'overview'
  | 'shipments'
  | 'bids'
  | 'interests'
  | 'vehicles'
  | 'ratings'
  | 'documents'
  | 'service-lanes'
  | 'account';

/** Plain words for a refused override (Contract C6); API text is never shown. */
function overrideFailureMessage(error: unknown): string {
  if (error instanceof DnmRequestFailure) {
    if (error.code === 'KYC_CHECK_OVERRIDE_NOT_ALLOWED')
      return 'This check can’t be overridden any more — its result changed. Close, refresh and look again.';
    if (error.code === 'REASON_REQUIRED') return 'Add a reason of 3 to 500 characters.';
  }
  return actionFailureMessage(error);
}

function vehicleStatusColor(status: string) {
  return status === 'ACTIVE'
    ? { bg: 'var(--status-green-bg)', text: 'var(--status-green-text)' }
    : { bg: 'var(--status-gray-bg)', text: 'var(--status-gray-text)' };
}

function fmtDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function fmtDateTime(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const isLicenseExpiringSoon = (expiryStr?: string) => {
  if (!expiryStr) return false;
  const expiry = new Date(expiryStr);
  const diffTime = expiry.getTime() - new Date().getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays >= 0 && diffDays < 30;
};

export function UserProfileView() {
  const { t } = useTranslation();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const userId = Number(id);

  const searchParams = new URLSearchParams(location.search);
  const tabFromQuery = searchParams.get('tab') as ProfileTab | null;
  const tabFromState = (
    location.state as { tab?: ProfileTab; page?: number; [key: string]: unknown } | null
  )?.tab;
  const pageFromQuery = searchParams.get('page') ? Number(searchParams.get('page')) : undefined;
  const pageFromState = (
    location.state as { tab?: ProfileTab; page?: number; [key: string]: unknown } | null
  )?.page;
  const initialPage =
    (!Number.isNaN(pageFromQuery) ? pageFromQuery : undefined) ?? pageFromState ?? 0;

  const [activeTab, setActiveTab] = useState<ProfileTab>(() => {
    if (tabFromQuery) return tabFromQuery;
    if (tabFromState) return tabFromState;
    return 'overview';
  });
  useEffect(() => {
    setActiveTab(tabFromQuery ?? tabFromState ?? 'overview');
  }, [userId, tabFromQuery, tabFromState]);
  const [shipmentsPage, setShipmentsPage] = useState(() =>
    activeTab === 'shipments' ? initialPage : 0,
  );
  const [bidsPage, setBidsPage] = useState(() => (activeTab === 'bids' ? initialPage : 0));
  const [ratingsPage, setRatingsPage] = useState(() => (activeTab === 'ratings' ? initialPage : 0));
  const [documentsPage, setDocumentsPage] = useState(() =>
    activeTab === 'documents' ? initialPage : 0,
  );

  const [isApproveOpen, setIsApproveOpen] = useState(false);
  /** The PAN / bank check whose override dialog is open (Contract C6). */
  const [overriding, setOverriding] = useState<AdminKycCheck | null>(null);
  const [overrideError, setOverrideError] = useState<string | null>(null);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editFocusId, setEditFocusId] = useState<string>();
  const editKyc = (focusId?: string) => {
    setEditFocusId(focusId);
    setIsApproveOpen(false);
    setIsEditOpen(true);
  };

  // Overview Query
  const {
    data: overview,
    isLoading: isOverviewLoading,
    isError: isOverviewError,
    refetch: refetchOverview,
  } = useQuery({
    queryKey: ['admin', 'users', 'overview', userId],
    queryFn: () => getUserOverview(userId),
    enabled: !isNaN(userId),
    ...staticQueryPolicy,
  });

  const role = overview?.user.role as UserRole;

  // PAN / bank check results (Contract C6): only for shippers and fleet managers, and only while
  // the KYC tab or the Approve dialog shows them. A failure stays inside the PAN section.
  const queryClient = useQueryClient();
  const checksKey = ['admin', 'users', userId, 'verification-checks'] as const;
  const checksQuery = useQuery({
    queryKey: checksKey,
    queryFn: () => getVerificationChecks(userId),
    enabled:
      !isNaN(userId) &&
      (role === 'SHIPPER' || role === 'FLEET_MANAGER') &&
      (activeTab === 'kyc' || isApproveOpen),
    ...staticQueryPolicy,
  });
  const checks: KycChecksProp = {
    data: checksQuery.data?.checks,
    isLoading: checksQuery.isLoading,
    isError: checksQuery.isError && !checksQuery.data,
    refetch: () => checksQuery.refetch(),
  };

  // Profile change requests (#281): only while the KYC tab is open. A failure stays inline.
  const changeRequestsQuery = useQuery({
    queryKey: ['admin', 'users', userId, 'change-requests'],
    queryFn: () => getUserChangeRequests(userId),
    enabled: !isNaN(userId) && activeTab === 'kyc',
    ...staticQueryPolicy,
  });

  // Tab Queries (only loaded when active)
  const shipmentsKey = ['admin', 'users', 'overview-shipments', userId, shipmentsPage] as const;
  const {
    data: shipmentsData,
    isLoading: isShipmentsLoading,
    isError: isShipmentsError,
    refetch: refetchShipments,
  } = useQuery({
    queryKey: shipmentsKey,
    queryFn: () => getUserOverviewShipments(userId, { page: shipmentsPage, size: 10 }),
    enabled: activeTab === 'shipments' && !isNaN(userId),
    ...historyQueryPolicy,
    placeholderData: safePagePlaceholder(shipmentsKey, 4),
  });

  const bidsKey = ['admin', 'users', 'overview-bids', userId, bidsPage] as const;
  const {
    data: bidsData,
    isLoading: isBidsLoading,
    isError: isBidsError,
    refetch: refetchBids,
  } = useQuery({
    queryKey: bidsKey,
    queryFn: () => getUserOverviewBids(userId, { page: bidsPage, size: 10 }),
    enabled:
      activeTab === 'bids' && !isNaN(userId) && (role === 'DRIVER' || role === 'FLEET_MANAGER'),
    ...historyQueryPolicy,
    placeholderData: safePagePlaceholder(bidsKey, 4),
  });

  const {
    data: vehiclesData,
    isLoading: isVehiclesLoading,
    isError: isVehiclesError,
    refetch: refetchVehicles,
  } = useQuery({
    queryKey: ['admin', 'users', 'overview-vehicles', userId],
    queryFn: () => getUserOverviewVehicles(userId),
    enabled: activeTab === 'vehicles' && !isNaN(userId) && role === 'DRIVER',
    ...staticQueryPolicy,
  });

  const ratingsKey = ['admin', 'users', 'overview-ratings', userId, ratingsPage] as const;
  const {
    data: ratingsData,
    isLoading: isRatingsLoading,
    isError: isRatingsError,
    refetch: refetchRatings,
  } = useQuery({
    queryKey: ratingsKey,
    queryFn: () => getUserOverviewRatings(userId, { page: ratingsPage, size: 10 }),
    enabled: activeTab === 'ratings' && !isNaN(userId),
    ...historyQueryPolicy,
    placeholderData: safePagePlaceholder(ratingsKey, 4),
  });

  const documentsKey = ['admin', 'users', 'overview-documents', userId, documentsPage] as const;
  const {
    data: documentsData,
    isLoading: isDocumentsLoading,
    isError: isDocumentsError,
    refetch: refetchDocuments,
  } = useQuery({
    queryKey: documentsKey,
    queryFn: () => getUserOverviewDocuments(userId, { page: documentsPage, size: 10 }),
    enabled: activeTab === 'documents' && !isNaN(userId),
    ...historyQueryPolicy,
    placeholderData: safePagePlaceholder(documentsKey, 4),
  });

  const {
    data: serviceLanesData,
    isLoading: isLanesLoading,
    isError: isLanesError,
    refetch: refetchLanes,
  } = useQuery({
    queryKey: ['admin', 'users', 'service-lanes', userId],
    queryFn: () => searchServiceLanes(),
    enabled: activeTab === 'service-lanes' && !isNaN(userId) && role === 'FLEET_MANAGER',
    ...referenceQueryPolicy,
  });

  const fleetLanes = serviceLanesData?.filter((lane) => lane.fleetManagerId === userId) ?? [];

  const [interestPage, setInterestPage] = useState(() =>
    activeTab === 'interests' ? initialPage : 0,
  );
  const [selectedInterestId, setSelectedInterestId] = useState<number | null>(null);

  const interestsKey = ['admin', 'interests', 'vendor', userId, interestPage] as const;
  const {
    data: userInterests,
    isLoading: isInterestsLoading,
    isError: isInterestsError,
    refetch: refetchInterests,
  } = useQuery({
    queryKey: interestsKey,
    queryFn: () => getAdminInterests({ vendorId: userId, page: interestPage, size: 10 }),
    enabled: role === 'FLEET_MANAGER' && activeTab === 'interests' && !isNaN(userId),
    ...historyQueryPolicy,
    placeholderData: safePagePlaceholder(interestsKey, 4),
  });

  /** Refusal reason for the open KYC / lane confirm; cleared on retry and close. */
  const [dialogError, setDialogError] = useState<string | null>(null);
  const kycMutation = useMutationHook(
    ({ status, reason }: { status: 'VERIFIED' | 'REJECTED'; reason?: string }) =>
      updateUserKyc(userId, status, reason),
    {
      successMessage: (data) => `Successfully updated KYC to ${codeLabel('kyc', data.kycStatus)}`,
      invalidateKeys: [
        ['admin', 'users'],
        ['admin', 'users', 'overview', userId],
        ['admin', 'dashboard'],
        ['payments'],
      ],
      errorMessage: actionFailureMessage,
      onSuccess: () => {
        setIsApproveOpen(false);
        setIsRejectOpen(false);
      },
      onError: (err) => setDialogError(actionFailureMessage(err)),
    },
  );

  const overrideMutation = useMutationHook(
    ({ checkType, reason }: { checkType: KycCheckType; reason: string }) =>
      overrideVerificationCheck(userId, checkType, reason),
    {
      successMessage: (data) =>
        data.checkType === 'BANK' ? 'Bank check overridden' : 'PAN check overridden',
      invalidateKeys: [[...checksKey]],
      errorMessage: overrideFailureMessage,
      onSuccess: () => setOverriding(null),
      onError: (err) => setOverrideError(overrideFailureMessage(err)),
    },
  );

  const [isAddLaneOpen, setIsAddLaneOpen] = useState(false);
  const [deletingLaneId, setDeletingLaneId] = useState<number | null>(null);

  const deleteLaneMutation = useMutationHook(
    (laneId: number) => adminDeleteServiceLane(userId, laneId),
    {
      successMessage: 'Service lane removed successfully.',
      invalidateKeys: [
        ['admin', 'users', 'service-lanes', userId],
        ['admin', 'service-lanes'],
      ],
      errorMessage: actionFailureMessage,
      onSuccess: () => {
        setDeletingLaneId(null);
      },
      onError: (err) => setDialogError(actionFailureMessage(err)),
    },
  );

  if (isNaN(userId)) {
    return <div className={styles.error}>{t('users.userProfileView.invalidId')}</div>;
  }

  if (isOverviewLoading) {
    return <LoadingState label={t('users.userProfileView.loading')} />;
  }

  if (!overview) {
    return (
      <ErrorState title={t('users.userProfileView.loadError')} onRetry={() => refetchOverview()} />
    );
  }

  const { user, profile, stats, recentShipments, recentRatings } = overview;
  const displayName = userDisplayName(user);
  const kycInfo = kycStatusColor(user.kycStatus as KycStatus);

  // Determine tabs list based on role
  const getTabsForRole = (): { value: ProfileTab; label: string }[] => {
    const list: { value: ProfileTab; label: string }[] = [
      { value: 'overview', label: 'Overview' },
      { value: 'kyc', label: 'KYC' },
      { value: 'shipments', label: 'Shipments' },
    ];
    if (role === 'DRIVER' || role === 'FLEET_MANAGER') {
      list.push({ value: 'bids', label: 'Bid(Rate)' });
    }
    if (role === 'DRIVER') {
      list.push({ value: 'vehicles', label: 'Vehicles' });
    }
    if (role === 'FLEET_MANAGER') {
      list.push({ value: 'interests', label: 'Interests' });
      list.push({ value: 'service-lanes', label: 'Service Lanes' });
    }
    list.push({ value: 'ratings', label: 'Ratings' });
    list.push({ value: 'documents', label: 'Documents' });
    list.push({ value: 'account', label: 'Account' });
    return list;
  };

  const tabs = getTabsForRole();

  // Helper back button target path
  const stateFrom = (location.state as { from?: string })?.from;

  const getActiveTabCurrentPage = () => {
    switch (activeTab) {
      case 'shipments':
        return shipmentsPage;
      case 'bids':
        return bidsPage;
      case 'ratings':
        return ratingsPage;
      case 'documents':
        return documentsPage;
      case 'interests':
        return interestPage;
      default:
        return 0;
    }
  };

  const currentTabPage = getActiveTabCurrentPage();
  const buildProfileReturnUrl = () => {
    const params = new URLSearchParams();
    if (activeTab !== 'overview') params.set('tab', activeTab);
    if (currentTabPage > 0) params.set('page', String(currentTabPage));
    const qs = params.toString();
    return qs ? `${location.pathname}?${qs}` : location.pathname;
  };

  const shipmentNavState = {
    from: buildProfileReturnUrl(),
    fromLabel: user.fullName || 'User',
    restoreState: { tab: activeTab, page: currentTabPage },
  };
  const getBackPath = () => {
    if (stateFrom) return stateFrom;
    if (role === 'DRIVER') return '/drivers';
    if (role === 'SHIPPER') return '/shippers';
    if (role === 'FLEET_MANAGER') return '/fleets';
    return '/';
  };

  const getBackLabel = () => {
    if (stateFrom) {
      if (stateFrom.startsWith('/shipments')) return '← Back to Shipment';
      return '← Back';
    }
    return '← Back to Users';
  };

  return (
    <div className={styles.page}>
      {isOverviewError && <StaleBanner onRetry={() => refetchOverview()} />}
      {/* Detail Header */}
      <div className={styles.header}>
        <Link to={getBackPath()} className={styles.backBtn} state={location.state}>
          {getBackLabel()}
        </Link>
        <div className={styles.headerRow}>
          <div>
            <div className={styles.titleArea}>
              {user.avatarUrl && (
                <ProfileMediaPreview
                  key={`${userId}:${user.avatarUrl}`}
                  userId={userId}
                  url={user.avatarUrl}
                  label={t('users.userProfileView.profilePhoto')}
                  autoLoad
                  compact
                />
              )}
              <h2 className={styles.title}>{displayName.headline}</h2>
              <span className={styles.roleTag}>{codeLabel('role', role)}</span>
            </div>
            <div className={styles.subtitle}>
              {displayName.secondary && (
                <>
                  <span>{displayName.secondary}</span>
                  <span className={styles.separator}>•</span>
                </>
              )}
              <span className={styles.mono}>{user.phoneNumber}</span>
              {user.email && <span className={styles.separator}>•</span>}
              {user.email && <span className={styles.mono}>{user.email}</span>}
              <span className={styles.separator}>•</span>
              <span className={styles.dateLabel}>{t('users.userProfileView.joined', { date: fmtDate(user.createdAt) })}</span>
            </div>
            {overview.linkedPersonas && overview.linkedPersonas.length > 0 && (
              <div className={styles.linkedPersonasRow} data-testid="linked-personas-row">
                <span className={styles.linkedLabel}>{t('users.userProfileView.alsoRegisteredAs')}</span>
                {overview.linkedPersonas.map((lp) => (
                  <Link
                    key={lp.id}
                    to={`/users/${lp.id}`}
                    className={styles.linkedPersonaBadge}
                    title={`Switch to ${codeLabel('role', lp.role)} profile`}
                  >
                    <span className={styles.linkedRole}>{codeLabel('role', lp.role)}</span>
                    <span className={styles.linkedKyc}>({codeLabel('kyc', lp.kycStatus)})</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
          <div className={styles.headerRight}>
            <div className={styles.statusGroup}>
              <StatusPill bg={kycInfo.bg} text={kycInfo.text} label={codeLabel('kyc', user.kycStatus)} />
              <span className={user.isActive ? styles.statusActive : styles.statusInactive}>
                {codeLabel('userStatus', user.status)}
              </span>
            </div>
            <div className={styles.controls}>
              {role !== 'ADMIN' && (
                <button
                  className={`${styles.btnControl} ${styles.btnEdit}`}
                  onClick={() => setIsEditOpen(true)}
                >
                  {t('users.userProfileView.edit')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* KPI Overview Strip (Role-aware) */}
      <div className={styles.kpiStrip}>
        <KpiCard label={t('users.userProfileView.kpiTotalShipments')} value={stats.shipmentsTotal} />
        <KpiCard label={t('users.userProfileView.kpiCompleted')} value={stats.shipmentsCompleted} accent="green" />
        <KpiCard label={t('users.userProfileView.kpiCancelled')} value={stats.shipmentsCancelled} accent="red" />

        {(role === 'DRIVER' || role === 'FLEET_MANAGER') && (
          <>
            <KpiCard label={t('users.userProfileView.kpiBidsPlaced')} value={stats.bidsTotal} />
            <KpiCard label={t('users.userProfileView.kpiBidsAccepted')} value={stats.bidsAccepted} accent="green" />
            <KpiCard
              label={t('users.userProfileView.kpiBidWinRate')}
              value={stats.bidWinRate !== null ? `${Math.round(stats.bidWinRate * 100)}%` : '—'}
              accent="amber"
            />
          </>
        )}

        {role === 'DRIVER' && (
          <KpiCard label={t('users.userProfileView.kpiVehiclesOwned')} value={stats.vehiclesCount} accent="default" />
        )}
      </div>

      {/* Tab Navigation */}
      <div className={styles.tabsContainer}>
        <div className={styles.tabs}>
          {tabs.map((t) => (
            <button
              key={t.value}
              className={
                activeTab === t.value ? `${styles.tabBtn} ${styles.tabActive}` : styles.tabBtn
              }
              onClick={() => setActiveTab(t.value)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className={styles.tabsRefresh}>
          <RefreshButton
            title={`Refresh ${activeTab}`}
            queryKeys={[
              ['admin', 'users', 'overview', userId],
              ...(activeTab === 'shipments'
                ? [['admin', 'users', 'overview-shipments', userId]]
                : []),
              ...(activeTab === 'bids' ? [['admin', 'users', 'overview-bids', userId]] : []),
              ...(activeTab === 'vehicles'
                ? [['admin', 'users', 'overview-vehicles', userId]]
                : []),
              ...(activeTab === 'ratings' ? [['admin', 'users', 'overview-ratings', userId]] : []),
              ...(activeTab === 'documents'
                ? [['admin', 'users', 'overview-documents', userId]]
                : []),
              ...(activeTab === 'service-lanes'
                ? [['admin', 'users', 'service-lanes', userId]]
                : []),
              ...(activeTab === 'interests' ? [['admin', 'interests', 'vendor', userId]] : []),
              ...(activeTab === 'kyc' ? [['admin', 'users', userId, 'change-requests']] : []),
            ]}
          />
        </div>
      </div>

      {activeTab === 'kyc' && (
        <section aria-label={t('users.userProfileView.kycReview')}>
          <button
            type="button"
            className={`${styles.btnControl} ${styles.btnEdit}`}
            onClick={() => editKyc()}
          >
            {t('users.userProfileView.editKyc')}
          </button>
          <KycReviewContent
            user={user}
            profile={
              profile as
                DriverProfileResponse | FleetManagerProfileResponse | ShipperProfileResponse | null
            }
            onEdit={editKyc}
            changeRequests={{
              data: changeRequestsQuery.data,
              isLoading: changeRequestsQuery.isLoading,
              isError: changeRequestsQuery.isError && !changeRequestsQuery.data,
              refetch: () => changeRequestsQuery.refetch(),
            }}
            checks={{
              ...checks,
              onOverride: (check) => {
                setOverrideError(null);
                setOverriding(check);
              },
            }}
          />
          <div className={styles.controls}>
            {user.kycStatus !== 'VERIFIED' && (
              <>
                <button
                  className={`${styles.btnControl} ${styles.btnApprove}`}
                  onClick={() => setIsApproveOpen(true)}
                >
                  {t('users.userProfileView.approveKyc')}
                </button>
                <button
                  className={`${styles.btnControl} ${styles.btnReject}`}
                  onClick={() => setIsRejectOpen(true)}
                >
                  {t('users.userProfileView.rejectKyc')}
                </button>
              </>
            )}
          </div>
        </section>
      )}
      {/* Tab Contents */}
      <div className={styles.tabContent}>
        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && role === 'SHIPPER' && (
          <ShipperPaymentTerms
            key={userId}
            userId={userId}
            gstin={(profile as ShipperProfileResponse)?.gstin}
            paymentTermsDays={(profile as ShipperProfileResponse)?.paymentTermsDays}
            gstMode={(profile as ShipperProfileResponse)?.gstMode}
            gstRatePct={(profile as ShipperProfileResponse)?.gstRatePct}
          />
        )}
        {/* Demo world only until the build issue wires rhino-api (#293). */}
        {activeTab === 'overview' && role === 'SHIPPER' && isDemo() && (
          <ShipperBillingConditions key={`billing-${userId}`} userId={userId} />
        )}
        {/* #298: managers only, demo world only until the build issue (Contract dnm-work#155). */}
        {activeTab === 'overview' && role === 'SHIPPER' && isDemo() && (
          <ShipperRateCard key={`rate-card-${userId}`} userId={userId} />
        )}
        {activeTab === 'overview' && (
          <div className={styles.grid}>
            {/* Sub-profile Details */}
            <div className={styles.spanFull}>
              <SectionCard title={t('users.userProfileView.subProfileDetails')}>
                {profile ? (
                  <div className={styles.profileFields}>
                    {role === 'DRIVER' && (
                      <>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.licenseNumber')}</div>
                          <div className={styles.fieldVal}>
                            {(profile as DriverProfileResponse).licenseNumber || '—'}
                          </div>
                        </div>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.licenseExpiry')}</div>
                          <div className={styles.fieldVal}>
                            {fmtDate((profile as DriverProfileResponse).licenseExpiry)}
                            {isLicenseExpiringSoon(
                              (profile as DriverProfileResponse).licenseExpiry,
                            ) && (
                              <span className={styles.warningBadge}>{t('users.userProfileView.expiringSoon')}</span>
                            )}
                          </div>
                        </div>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.availability')}</div>
                          <div className={styles.fieldVal}>
                            <span
                              className={
                                (profile as DriverProfileResponse).isAvailable
                                  ? styles.statusActive
                                  : styles.statusInactive
                              }
                            >
                              {(profile as DriverProfileResponse).isAvailable
                                ? 'Available'
                                : 'Unavailable'}
                            </span>
                          </div>
                        </div>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.currentVehicle')}</div>
                          <div className={styles.fieldVal}>
                            {(profile as DriverProfileResponse).vehicleNumber || '—'}
                          </div>
                        </div>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.capacity')}</div>
                          <div className={styles.fieldVal}>
                            {tons((profile as DriverProfileResponse).vehicleCapacityKg)}
                          </div>
                        </div>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.drivingLicenseDocument')}</div>
                          <div className={styles.fieldVal}>
                            {(profile as DriverProfileResponse).dlDocumentUrl ? (
                              <span>{t('users.userProfileView.attachedOpenKyc')}</span>
                            ) : (
                              '—'
                            )}
                          </div>
                        </div>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.vehicleRcDocument')}</div>
                          <div className={styles.fieldVal}>
                            {(profile as DriverProfileResponse).rcDocumentUrl ? (
                              <span>{t('users.userProfileView.attachedOpenKyc')}</span>
                            ) : (
                              '—'
                            )}
                          </div>
                        </div>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.lastKnownLocation')}</div>
                          <div className={styles.fieldVal}>
                            {(profile as DriverProfileResponse).lastLatitude &&
                            (profile as DriverProfileResponse).lastLongitude ? (
                              <a
                                href={`https://www.google.com/maps/search/?api=1&query=${(profile as DriverProfileResponse).lastLatitude},${(profile as DriverProfileResponse).lastLongitude}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.mapLink}
                              >
                                {t('users.userProfileView.maps', { lat: (profile as DriverProfileResponse).lastLatitude!.toFixed(5), lng: (profile as DriverProfileResponse).lastLongitude!.toFixed(5) })}
                              </a>
                            ) : (
                              'No location recorded'
                            )}
                            {(profile as DriverProfileResponse).locationUpdatedAt && (
                              <span className={styles.locationTime}>
                                {t('users.userProfileView.locationUpdated', { date: fmtDateTime((profile as DriverProfileResponse).locationUpdatedAt) })}
                              </span>
                            )}
                          </div>
                        </div>
                      </>
                    )}

                    {role === 'SHIPPER' && (
                      <>
                        <div className={styles.profileField} style={{ gridColumn: '1 / -1' }}>
                          <AccountManagerSection shipperId={userId} />
                        </div>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.companyName')}</div>
                          <div className={styles.fieldVal}>
                            {(profile as ShipperProfileResponse).companyName || '—'}
                          </div>
                        </div>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.gstin')}</div>
                          <div className={`${styles.fieldVal} ${styles.mono}`}>
                            {(profile as ShipperProfileResponse).gstin || '—'}
                          </div>
                        </div>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.corporatePan')}</div>
                          <div className={`${styles.fieldVal} ${styles.mono}`}>
                            {(profile as ShipperProfileResponse).panNumber || '—'}
                          </div>
                        </div>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.secondaryPhone')}</div>
                          <div className={styles.fieldVal}>
                            {(profile as ShipperProfileResponse).secondaryPhoneNumber || '—'}
                          </div>
                        </div>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.whatsAppNumber')}</div>
                          <div className={styles.fieldVal}>
                            {(profile as ShipperProfileResponse).whatsappNumber || '—'}
                          </div>
                        </div>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.billingAddress')}</div>
                          <div className={styles.fieldVal}>
                            {(profile as ShipperProfileResponse).billingAddressText ||
                              ((profile as ShipperProfileResponse).billingAddress
                                ? `${(profile as ShipperProfileResponse).billingAddress!.line}, ${(profile as ShipperProfileResponse).billingAddress!.city}, ${(profile as ShipperProfileResponse).billingAddress!.state} - ${(profile as ShipperProfileResponse).billingAddress!.pincode}`
                                : '—')}
                          </div>
                        </div>
                      </>
                    )}

                    {role === 'FLEET_MANAGER' && (
                      <>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.companyName')}</div>
                          <div className={styles.fieldVal}>
                            {(profile as FleetManagerProfileResponse).companyName || '—'}
                          </div>
                        </div>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.gstin')}</div>
                          <div className={`${styles.fieldVal} ${styles.mono}`}>
                            {(profile as FleetManagerProfileResponse).gstin || '—'}
                          </div>
                        </div>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.corporatePan')}</div>
                          <div className={`${styles.fieldVal} ${styles.mono}`}>
                            {(profile as FleetManagerProfileResponse).panNumber || '—'}
                          </div>
                        </div>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.serviceRegions')}</div>
                          <div className={styles.fieldVal}>
                            {(profile as FleetManagerProfileResponse).serviceRegions || '—'}
                          </div>
                        </div>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.fleetSize')}</div>
                          <div className={styles.fieldVal}>
                            {(profile as FleetManagerProfileResponse).fleetSize || '—'}
                          </div>
                        </div>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.secondaryPhone')}</div>
                          <div className={styles.fieldVal}>
                            {(profile as FleetManagerProfileResponse).secondaryPhoneNumber || '—'}
                          </div>
                        </div>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.whatsAppNumber')}</div>
                          <div className={styles.fieldVal}>
                            {(profile as FleetManagerProfileResponse).whatsappNumber || '—'}
                          </div>
                        </div>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.billingAddress')}</div>
                          <div className={styles.fieldVal}>
                            {(profile as FleetManagerProfileResponse).billingAddress || '—'}
                          </div>
                        </div>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.bankName')}</div>
                          <div className={styles.fieldVal}>
                            {(profile as FleetManagerProfileResponse).bankName || '—'}
                          </div>
                        </div>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.accountHolder')}</div>
                          <div className={styles.fieldVal}>
                            {(profile as FleetManagerProfileResponse).accountHolderName || '—'}
                          </div>
                        </div>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.accountNumber')}</div>
                          <div className={`${styles.fieldVal} ${styles.mono}`}>
                            {(profile as FleetManagerProfileResponse).accountNumber || '—'}
                          </div>
                        </div>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.ifscCode')}</div>
                          <div className={`${styles.fieldVal} ${styles.mono}`}>
                            {(profile as FleetManagerProfileResponse).ifscCode || '—'}
                          </div>
                        </div>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.bankProofType')}</div>
                          <div className={styles.fieldVal}>
                            {(profile as FleetManagerProfileResponse).bankProofType || '—'}
                          </div>
                        </div>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.bankProofDocument')}</div>
                          <div className={styles.fieldVal}>
                            {(profile as FleetManagerProfileResponse).bankProofUrl ? (
                              <span>{t('users.userProfileView.attachedOpenKyc')}</span>
                            ) : (
                              '—'
                            )}
                          </div>
                        </div>
                        <div className={styles.profileField}>
                          <div className={styles.fieldLabel}>{t('users.userProfileView.payoutVerification')}</div>
                          <div className={styles.fieldVal}>
                            {(profile as FleetManagerProfileResponse).verificationStatus || '—'}
                          </div>
                        </div>
                        {(profile as FleetManagerProfileResponse).tdsDeclaration && (
                          <div className={styles.profileField}>
                            <div className={styles.fieldLabel}>{t('users.userProfileView.tds194c')}</div>
                            <div className={styles.fieldVal}>
                              <TdsDeclarationPill
                                declaration={(profile as FleetManagerProfileResponse).tdsDeclaration}
                              />
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <div className={styles.emptyProfile}>{t('users.userProfileView.noSubProfile')}</div>
                )}
              </SectionCard>
            </div>

            {/* Recent Shipments */}
            <div>
              <SectionCard title={t('users.userProfileView.recentShipments')}>
                <DataTable
                  rows={recentShipments}
                  keyFn={(s) => s.id}
                  empty="No shipments recorded"
                  columns={[
                    {
                      key: 'id',
                      header: 'ID',
                      width: '64px',
                      render: (s) => (
                        <Link
                          to={`/shipments/${s.id}`}
                          state={shipmentNavState}
                          className={styles.link}
                        >
                          #{s.id}
                        </Link>
                      ),
                    },
                    {
                      key: 'route',
                      header: 'Route',
                      render: (s) => (
                        <RouteText route={s} />
                      ),
                    },
                    {
                      key: 'pickupDate',
                      header: 'Pickup Date',
                      render: (s) => fmtDate(s.pickupDate),
                    },
                    {
                      key: 'status',
                      header: 'Status',
                      render: (s) => {
                        const colors = shipmentStatusColor(s.status);
                        return <StatusPill bg={colors.bg} text={colors.text} label={shipmentStatusLabel(s.status)} title={s.status} />;
                      },
                    },
                  ]}
                />
              </SectionCard>
            </div>

            {/* Recent Ratings */}
            <div>
              <SectionCard title={t('users.userProfileView.recentRatings')}>
                <DataTable
                  rows={recentRatings}
                  keyFn={(r) => r.id}
                  empty="No ratings received yet"
                  columns={[
                    {
                      key: 'score',
                      header: 'Score',
                      width: '80px',
                      render: (r) => <span className={styles.ratingScore}>★ {r.score}/5</span>,
                    },
                    {
                      key: 'raterName',
                      header: 'Rater',
                      render: (r) => r.raterName || 'System',
                    },
                    {
                      key: 'comment',
                      header: 'Comment',
                      render: (r) => <span className={styles.commentText}>{r.comment || '—'}</span>,
                    },
                    {
                      key: 'shipmentId',
                      header: 'Shipment',
                      width: '90px',
                      render: (r) => (
                        <Link
                          to={`/shipments/${r.shipmentId}`}
                          state={shipmentNavState}
                          className={styles.link}
                        >
                          #{r.shipmentId}
                        </Link>
                      ),
                    },
                  ]}
                />
              </SectionCard>
            </div>
          </div>
        )}

        {/* SHIPMENTS TAB */}
        {activeTab === 'shipments' && (
          <SectionCard>
            <DataTable
              loading={isShipmentsLoading}
              error={isShipmentsError ? 'We couldn’t load shipments' : undefined}
              onRetry={() => refetchShipments()}
              rows={shipmentsData?.items ?? []}
              keyFn={(s) => s.id}
              empty="No shipments recorded"
              columns={[
                {
                  key: 'id',
                  header: 'ID',
                  width: '64px',
                  render: (s) => (
                    <Link
                      to={`/shipments/${s.id}`}
                      state={shipmentNavState}
                      className={styles.link}
                    >
                      #{s.id}
                    </Link>
                  ),
                },
                {
                  key: 'origin',
                  header: 'Origin',
                  render: (s) => routeEnd(s.originCity, s.originState, 'Origin'),
                },
                {
                  key: 'destination',
                  header: 'Destination',
                  render: (s) => routeEnd(s.destinationCity, s.destinationState, 'Destination'),
                },
                {
                  key: 'cargo',
                  header: 'Cargo Description',
                  render: (s) => s.cargoDescription,
                },
                {
                  key: 'pickupDate',
                  header: 'Pickup Date',
                  render: (s) => fmtDate(s.pickupDate),
                },
                {
                  key: 'finalPrice',
                  header: 'Final Price',
                  render: (s) => {
                    // #288, R36: before award the partner price (or "—"), never a margin-derived carrier figure.
                    if (s.finalPrice == null) {
                      return (
                        <span>
                          {curr(s.offeredPrice)}
                          <span className={styles.priceSub}>{partnerPriceLabel(s.partnerOfferPrice)}</span>
                        </span>
                      );
                    }
                    const dual = dualPrice(s.finalPrice, s.shipperFacingFinalPrice);
                    return (
                      <span>
                        {dual.original}
                        {dual.adjusted && <span className={styles.priceSub}>{t('users.userProfileView.withMargin', { amount: dual.adjusted })}</span>}
                      </span>
                    );
                  },
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (s) => {
                    const colors = shipmentStatusColor(s.status);
                    return <StatusPill bg={colors.bg} text={colors.text} label={shipmentStatusLabel(s.status)} title={s.status} />;
                  },
                },
              ]}
            />
            {shipmentsData && (
              <Pagination
                page={shipmentsData.page}
                totalPages={shipmentsData.totalPages}
                totalItems={shipmentsData.totalItems}
                size={shipmentsData.size}
                onPage={setShipmentsPage}
              />
            )}
          </SectionCard>
        )}

        {/* BIDS TAB */}
        {activeTab === 'bids' && (
          <SectionCard>
            <DataTable
              loading={isBidsLoading}
              error={isBidsError ? 'We couldn’t load bids' : undefined}
              onRetry={() => refetchBids()}
              rows={bidsData?.items ?? []}
              keyFn={(b) => b.id}
              empty="No bids placed yet"
              columns={[
                {
                  key: 'id',
                  header: 'Bid ID',
                  width: '80px',
                  render: (b) => <span className={styles.mono}>#{b.id}</span>,
                },
                {
                  key: 'shipmentId',
                  header: 'Shipment',
                  width: '90px',
                  render: (b) => (
                    <Link
                      to={`/shipments/${b.shipmentId}`}
                      state={shipmentNavState}
                      className={styles.link}
                    >
                      #{b.shipmentId}
                    </Link>
                  ),
                },
                {
                  key: 'amount',
                  header: 'Amount',
                  render: (b) => {
                    const dual = dualPrice(b.amount, b.shipperFacingAmount);
                    return (
                      <span>
                        {dual.original}
                        {dual.adjusted && (
                          <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)' }}>
                            {t('users.userProfileView.shipperFacing', { amount: dual.adjusted })}
                          </span>
                        )}
                      </span>
                    );
                  },
                },
                {
                  key: 'etaHours',
                  header: 'ETA (hrs)',
                  render: (b) => `${b.etaHours}h`,
                },
                {
                  key: 'status',
                  header: 'Bid Status',
                  render: (b) => {
                    const pill = bidStatusDisplay(b.status);
                    return (
                      <StatusPill
                        bg={pill.bg}
                        text={pill.text}
                        label={pill.label}
                        icon={pill.icon && <StatusIcon name={pill.icon} />}
                        title={b.status}
                      />
                    );
                  },
                },
                {
                  key: 'createdAt',
                  header: 'Created At',
                  render: (b) => fmtDateTime(b.createdAt),
                },
              ]}
            />
            {bidsData && (
              <Pagination
                page={bidsData.page}
                totalPages={bidsData.totalPages}
                totalItems={bidsData.totalItems}
                size={bidsData.size}
                onPage={setBidsPage}
              />
            )}
          </SectionCard>
        )}

        {/* VEHICLES TAB */}
        {activeTab === 'vehicles' && (
          <SectionCard>
            <DataTable
              loading={isVehiclesLoading}
              error={isVehiclesError ? 'We couldn’t load vehicles' : undefined}
              onRetry={() => refetchVehicles()}
              rows={vehiclesData ?? []}
              keyFn={(v) => v.id}
              empty="No vehicles registered"
              columns={[
                {
                  key: 'id',
                  header: 'ID',
                  width: '64px',
                  render: (v) => <span className={styles.mono}>#{v.id}</span>,
                },
                {
                  key: 'registrationNumber',
                  header: 'Reg Number',
                  render: (v) => <span className={styles.mono}>{v.registrationNumber}</span>,
                },
                {
                  key: 'type',
                  header: 'Type',
                  render: (v) => v.type,
                },
                {
                  key: 'capacityWeight',
                  header: 'Weight Capacity',
                  render: (v) => tons(v.capacityWeight),
                },
                {
                  key: 'capacityVolume',
                  header: 'Volume Capacity',
                  render: (v) => (v.capacityVolume ? `${v.capacityVolume} cbm` : '—'),
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (v) => {
                    const colors = vehicleStatusColor(v.status);
                    return <StatusPill bg={colors.bg} text={colors.text} label={codeLabel('vehicleStatus', v.status)} />;
                  },
                },
                {
                  key: 'createdAt',
                  header: 'Created',
                  render: (v) => fmtDate(v.createdAt),
                },
              ]}
            />
          </SectionCard>
        )}

        {/* RATINGS TAB */}
        {activeTab === 'ratings' && (
          <SectionCard>
            <DataTable
              loading={isRatingsLoading}
              error={isRatingsError ? 'We couldn’t load ratings' : undefined}
              onRetry={() => refetchRatings()}
              rows={ratingsData?.items ?? []}
              keyFn={(r) => r.id}
              empty="No ratings received yet"
              columns={[
                {
                  key: 'id',
                  header: 'ID',
                  width: '64px',
                  render: (r) => <span className={styles.mono}>#{r.id}</span>,
                },
                {
                  key: 'score',
                  header: 'Score',
                  render: (r) => <span className={styles.ratingScore}>★ {r.score}/5</span>,
                },
                {
                  key: 'raterName',
                  header: 'Rater Name',
                  render: (r) => r.raterName || 'System',
                },
                {
                  key: 'comment',
                  header: 'Comment',
                  render: (r) => <span className={styles.commentText}>{r.comment || '—'}</span>,
                },
                {
                  key: 'shipmentId',
                  header: 'Shipment',
                  render: (r) => (
                    <Link
                      to={`/shipments/${r.shipmentId}`}
                      state={shipmentNavState}
                      className={styles.link}
                    >
                      #{r.shipmentId}
                    </Link>
                  ),
                },
                {
                  key: 'createdAt',
                  header: 'Rated On',
                  render: (r) => fmtDateTime(r.createdAt),
                },
              ]}
            />
            {ratingsData && (
              <Pagination
                page={ratingsData.page}
                totalPages={ratingsData.totalPages}
                totalItems={ratingsData.totalItems}
                size={ratingsData.size}
                onPage={setRatingsPage}
              />
            )}
          </SectionCard>
        )}

        {/* DOCUMENTS TAB */}
        {activeTab === 'documents' && (
          <SectionCard>
            <DataTable
              loading={isDocumentsLoading}
              error={isDocumentsError ? 'We couldn’t load documents' : undefined}
              onRetry={() => refetchDocuments()}
              rows={documentsData?.items ?? []}
              keyFn={(d) => d.id}
              empty="No documents uploaded"
              columns={[
                {
                  key: 'id',
                  header: 'ID',
                  width: '64px',
                  render: (d) => <span className={styles.mono}>#{d.id}</span>,
                },
                {
                  key: 'shipmentId',
                  header: 'Shipment / vehicle',
                  render: (d) => {
                    const subject = documentSubject(d);
                    if (subject.kind === 'shipment') {
                      return (
                        <Link
                          to={`/shipments/${subject.shipmentId}`}
                          state={shipmentNavState}
                          className={styles.link}
                        >
                          #{subject.shipmentId}
                        </Link>
                      );
                    }
                    return subject.kind === 'vehicle' ? subject.label : '—';
                  },
                },
                {
                  key: 'type',
                  header: 'Document Type',
                  render: (d) => d.type,
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (d) => {
                    const colors = documentStatusColor(d.status);
                    return <StatusPill bg={colors.bg} text={colors.text} label={codeLabel('documentStatus', d.status)} />;
                  },
                },
                {
                  key: 'uploadedAt',
                  header: 'Uploaded At',
                  render: (d) => fmtDateTime(d.uploadedAt),
                },
                {
                  key: 'fileSize',
                  header: 'Size',
                  render: (d) => (d.fileSize ? `${(d.fileSize / 1024).toFixed(1)} KB` : '—'),
                },
                {
                  key: 'actions',
                  header: 'Download / Preview',
                  render: (d) => (
                    <a
                      href={d.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.link}
                    >
                      {t('users.userProfileView.openFile')}
                    </a>
                  ),
                },
              ]}
            />
            {documentsData && (
              <Pagination
                page={documentsData.page}
                totalPages={documentsData.totalPages}
                totalItems={documentsData.totalItems}
                size={documentsData.size}
                onPage={setDocumentsPage}
              />
            )}
          </SectionCard>
        )}

        {/* INTERESTS TAB */}
        {activeTab === 'interests' && (
          <SectionCard>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--ink)' }}>
                {t('users.userProfileView.vendorInterests', { total: userInterests?.totalItems ?? 0 })}
              </h3>
            </div>
            <DataTable
              loading={isInterestsLoading}
              error={isInterestsError ? 'We couldn’t load interests' : undefined}
              onRetry={() => refetchInterests()}
              rows={userInterests?.items ?? []}
              keyFn={(it) => it.id}
              empty="No load interests expressed by this partner"
              columns={[
                {
                  key: 'shipment',
                  header: 'Load / Route',
                  render: (it) => (
                    <div>
                      <Link
                        to={`/shipments/${it.shipmentId}`}
                        state={shipmentNavState}
                        style={{ color: 'var(--accent)', fontWeight: 600 }}
                      >
                        {it.shipmentUuid}
                      </Link>
                      <div style={{ fontSize: 12, color: 'var(--ink)', marginTop: 2 }}>
                        {it.routeLabel ||
                          `${it.pickupCity || '—'}, ${it.pickupStateName || it.pickupState || '—'} → ${it.dropCity || '—'}, ${it.dropStateName || it.dropState || '—'}`}
                      </div>
                    </div>
                  ),
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (it) => {
                    const colors = interestStatusColor(it.status);
                    return (
                      <StatusPill
                        label={codeLabel('interestStatus', it.status)}
                        bg={colors.bg}
                        text={colors.text}
                      />
                    );
                  },
                },
                {
                  key: 'owner',
                  header: 'Assigned Owner',
                  render: (it) => it.ownerAdminName || 'Unassigned',
                },
                {
                  key: 'nextFollowUp',
                  header: 'Next Follow-up',
                  render: (it) => fmtDateTime(it.nextFollowUpAt),
                },
                {
                  key: 'vendorNote',
                  header: 'Partner note',
                  render: (it) => (
                    <span
                      style={{
                        display: 'inline-block',
                        maxWidth: '220px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={it.vendorNote || ''}
                    >
                      {it.vendorNote || '—'}
                    </span>
                  ),
                },
                {
                  key: 'actions',
                  header: 'Actions',
                  render: (it) => (
                    <button
                      type="button"
                      className={styles.btnControl}
                      style={{ padding: '4px 10px', fontSize: 12 }}
                      onClick={() => setSelectedInterestId(it.id)}
                    >
                      {t('users.userProfileView.manage')}
                    </button>
                  ),
                },
              ]}
            />
            {userInterests && (
              <Pagination
                page={userInterests.page}
                totalPages={userInterests.totalPages}
                totalItems={userInterests.totalItems}
                size={userInterests.size}
                onPage={setInterestPage}
              />
            )}
          </SectionCard>
        )}

        {/* SERVICE LANES TAB */}
        {activeTab === 'service-lanes' && (
          <SectionCard>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--ink)' }}>
                {t('users.userProfileView.serviceLanesConfiguration')}
              </h3>
              <button
                className={`${styles.btnControl} ${styles.btnApprove}`}
                onClick={() => setIsAddLaneOpen(true)}
              >
                {t('users.userProfileView.addServiceLane')}
              </button>
            </div>
            <DataTable
              loading={isLanesLoading}
              error={isLanesError ? 'We couldn’t load lanes' : undefined}
              onRetry={() => refetchLanes()}
              rows={fleetLanes}
              keyFn={(l) => l.laneId}
              empty="No service lanes configured"
              columns={[
                {
                  key: 'laneId',
                  header: 'ID',
                  width: '64px',
                  render: (l) => <span className={styles.mono}>#{l.laneId}</span>,
                },
                {
                  key: 'fromState',
                  header: 'Origin State',
                  render: (l) => formatStateWithCapital(l.fromState),
                },
                {
                  key: 'toState',
                  header: 'Destination State',
                  render: (l) => formatStateWithCapital(l.toState),
                },
                {
                  key: 'truckTypes',
                  header: 'Truck Types Offered',
                  render: (l) => (
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {l.truckTypes.map((t) => (
                        <span
                          key={t}
                          style={{
                            fontSize: '11px',
                            background: 'var(--status-gray-bg)',
                            color: 'var(--status-gray-text)',
                            padding: '2px 6px',
                            borderRadius: '4px',
                          }}
                        >
                          {t.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  ),
                },
                {
                  key: 'actions',
                  header: 'Actions',
                  width: '90px',
                  render: (l) => (
                    <button
                      className={`${styles.btnControl} ${styles.btnReject}`}
                      style={{ padding: '4px 8px', fontSize: '11px' }}
                      onClick={() => setDeletingLaneId(l.laneId)}
                    >
                      {t('users.userProfileView.delete')}
                    </button>
                  ),
                },
              ]}
            />
          </SectionCard>
        )}

        {activeTab === 'account' && (
          <div className={styles.grid}>
            <div className={styles.spanFull}>
              <SectionCard title={t('users.userProfileView.unifiedAccount')}>
                {overview.account ? (
                  <div className={styles.profileFields}>
                    <div className={styles.profileField}>
                      <div className={styles.fieldLabel}>{t('users.userProfileView.accountId')}</div>
                      <div className={`${styles.fieldVal} ${styles.mono}`}>
                        #{overview.account.id}
                      </div>
                    </div>
                    <div className={styles.profileField}>
                      <div className={styles.fieldLabel}>{t('users.userProfileView.primaryPhone')}</div>
                      <div className={`${styles.fieldVal} ${styles.mono}`}>
                        {overview.account.phoneNumber}
                      </div>
                    </div>
                    <div className={styles.profileField}>
                      <div className={styles.fieldLabel}>{t('users.userProfileView.registeredOn')}</div>
                      <div className={styles.fieldVal}>{fmtDate(overview.account.createdAt)}</div>
                    </div>
                    <div className={styles.profileField}>
                      <div className={styles.fieldLabel}>{t('users.userProfileView.linkedPersonas')}</div>
                      <div className={styles.fieldVal}>
                        <div className={styles.linkedPersonasRow} style={{ marginTop: 0 }}>
                          <span
                            className={styles.linkedPersonaBadge}
                            style={{ background: 'var(--accent)', color: '#fff' }}
                          >
                            <span className={styles.linkedRole} style={{ color: '#fff' }}>
                              {t('users.userProfileView.currentRole', { role: codeLabel('role', role) })}
                            </span>
                            <span
                              className={styles.linkedKyc}
                              style={{ color: 'rgba(255,255,255,0.8)' }}
                            >
                              ({codeLabel('kyc', user.kycStatus)})
                            </span>
                          </span>
                          {overview.linkedPersonas &&
                            overview.linkedPersonas.map((lp) => (
                              <Link
                                key={lp.id}
                                to={`/users/${lp.id}`}
                                className={styles.linkedPersonaBadge}
                                title={`Switch to ${codeLabel('role', lp.role)} profile`}
                              >
                                <span className={styles.linkedRole}>
                                  {codeLabel('role', lp.role)}
                                </span>
                                <span className={styles.linkedKyc}>({codeLabel('kyc', lp.kycStatus)})</span>
                              </Link>
                            ))}
                        </div>
                      </div>
                    </div>
                    <div className={styles.profileField} style={{ gridColumn: '1 / -1' }}>
                      <div className={styles.fieldLabel}>{t('users.userProfileView.connectedPushDevices')}</div>
                      <div className={styles.fieldVal} style={{ width: '100%' }}>
                        {overview.account.devices && overview.account.devices.length > 0 ? (
                          <div className={styles.deviceList} style={{ width: '100%' }}>
                            {overview.account.devices.map((d) => (
                              <div
                                key={d.id}
                                className={styles.deviceItem}
                                data-testid={`account-device-${d.id}`}
                              >
                                <span className={styles.deviceApp}>
                                  {d.appType.replace(/_/g, ' ')}
                                </span>
                                <span className={styles.deviceId} title={d.deviceId}>
                                  {t('users.userProfileView.deviceToken', { id: d.deviceId })}
                                </span>
                                <span className={styles.deviceTime}>
                                  {t('users.userProfileView.lastSynced', { date: fmtDateTime(d.updatedAt) })}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span style={{ color: 'var(--muted)', fontSize: '13px' }}>
                            {t('users.userProfileView.noPushDevices')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={styles.emptyProfile}>
                    {t('users.userProfileView.noUnifiedAccount')}
                  </div>
                )}
              </SectionCard>
            </div>
          </div>
        )}
      </div>

      {/* KYC Approve Dialog */}
      <ConfirmDialog
        help="kyc-review"
        wide
        isOpen={isApproveOpen}
        title={t('users.userProfileView.approveKycTitle')}
        description={
          <>
            <p>{t('users.userProfileView.approveKycDescription')}</p>
            <KycReviewContent
              user={user}
              profile={
                profile as
                  | DriverProfileResponse
                  | FleetManagerProfileResponse
                  | ShipperProfileResponse
                  | null
              }
              onEdit={editKyc}
              checks={checks}
            />
          </>
        }
        confirmText="Verify User"
        cancelText="Cancel"
        variant="success"
        loading={kycMutation.isLoading}
        errorMessage={dialogError}
        onConfirm={() => {
          setDialogError(null);
          kycMutation.mutate({ status: 'VERIFIED' });
        }}
        onCancel={() => {
          setDialogError(null);
          setIsApproveOpen(false);
        }}
      />

      {/* PAN / bank check override (Contract C6): reason 3–500, saved in the audit log */}
      <ConfirmDialog
        help="kyc-review"
        isOpen={overriding !== null}
        title={overriding?.checkType === 'BANK' ? 'Override bank check' : 'Override PAN check'}
        description={
          overriding ? (
            <>
              <p>
                {t('users.userProfileView.currentResult')}{' '}
                {kycCheckDisplay(overriding)
                  .pills.map((p) => p.label)
                  .join(' · ')}
              </p>
              <p>{t('users.userProfileView.overrideDescription')}</p>
            </>
          ) : null
        }
        confirmText="Mark as verified"
        cancelText="Cancel"
        variant="primary"
        requireReason
        reasonPlaceholder="Why is this check correct? e.g. name matches the cancelled cheque"
        reasonMinLength={3}
        reasonMaxLength={500}
        loading={overrideMutation.isLoading}
        errorMessage={overrideError}
        onConfirm={(reason) => {
          if (!overriding || !reason) return;
          setOverrideError(null);
          overrideMutation.mutate({ checkType: overriding.checkType, reason });
        }}
        onCancel={() => {
          setOverrideError(null);
          setOverriding(null);
        }}
      />

      {/* KYC Reject Dialog */}
      <ConfirmDialog
        help="kyc-review"
        isOpen={isRejectOpen}
        title={t('users.userProfileView.rejectKycTitle')}
        description="Please provide a reason for rejecting this user's KYC verification. This reason will be visible to the user."
        confirmText="Reject"
        cancelText="Cancel"
        variant="danger"
        requireReason={true}
        reasonPlaceholder="Enter rejection reason..."
        loading={kycMutation.isLoading}
        errorMessage={dialogError}
        onConfirm={(reason) => {
          setDialogError(null);
          kycMutation.mutate({ status: 'REJECTED', reason });
        }}
        onCancel={() => {
          setDialogError(null);
          setIsRejectOpen(false);
        }}
      />

      {/* Edit User Modal */}
      {isEditOpen && role !== 'ADMIN' && (
        <UserFormModal
          role={role as Exclude<UserRole, 'ADMIN'>}
          user={user}
          initialFocusId={editFocusId}
          initialProfile={
            profile as
              DriverProfileResponse | FleetManagerProfileResponse | ShipperProfileResponse | null
          }
          onClose={() => setIsEditOpen(false)}
          onSaved={() => {
            // Re-fetch overview; an admin edit re-runs the PAN / bank checks (C3 ADMIN_EDIT).
            overviewServiceQueryInvalidator();
            void queryClient.invalidateQueries({ queryKey: [...checksKey] });
          }}
        />
      )}

      {/* Add Service Lane Modal */}
      {isAddLaneOpen && (
        <AddServiceLaneModal fleetManagerId={userId} onClose={() => setIsAddLaneOpen(false)} />
      )}

      {/* Delete Service Lane Dialog */}
      <ConfirmDialog
        help="service-lane"
        isOpen={deletingLaneId !== null}
        title={t('users.userProfileView.removeServiceLaneTitle')}
        description="Are you sure you want to remove this service lane from the partner's profile? This action cannot be undone."
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
        loading={deleteLaneMutation.isLoading}
        errorMessage={dialogError}
        onConfirm={() => {
          setDialogError(null);
          if (deletingLaneId !== null) {
            deleteLaneMutation.mutate(deletingLaneId);
          }
        }}
        onCancel={() => {
          setDialogError(null);
          setDeletingLaneId(null);
        }}
      />

      {selectedInterestId && (
        <InterestDetailModal
          interestId={selectedInterestId}
          onClose={() => setSelectedInterestId(null)}
          returnNavState={shipmentNavState}
        />
      )}
    </div>
  );

  function overviewServiceQueryInvalidator() {
    // Queries will automatically be invalidated via standard query client keys
  }
}
